import * as React from "react";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";

// material-ui
import Card from "@material-ui/core/Card";

import { List, ReferenceArrayInput, ReferenceInput, SelectInput, AutoCompleteArrayInput, FilterButton, Filter, translate } from "jazasoft";

import MuiTable from "../../../components/MuiTable";
import Tabs from "../../../components/tab/Tabs";
import SubTab from "../../../components/tab/SubTab";

import { useDeepMemo, useQuery, useSafeState, useSetting, useUnit } from "../../../hooks";
import { getStateChoices } from "../../../constants";
import { inputOptions, isEmpty, isTrue, listFromCsv } from "../../../utils/helpers";
import SettingKeys from "../../../utils/SettingKeys";
import { OState } from "../../../utils/types";

const OrderStateValue = Object.keys(OState)
  .map(key => OState[key])
  .reduce((acc, e) => ({ ...acc, [e.state]: e.stateValue }), {});

const renderState = record => {
  const stateList = record?.states?.split(",") || [];
  const state = stateList?.map(state => ({ state, value: OrderStateValue[state] })).sort((a, b) => a.value - b.value)[0]?.state || "-";
  return state + `${stateList?.length > 1 ? "*" : ""}`;
};

const parse = tabIdx => args => {
  const { state, productName, seasonId, customerId, season, customers, unitId } = args;
  if (tabIdx === 0) {
    return {
      unitId: unitId === "all" ? null : unitId,
      customer: customers,
      season,
      products: isEmpty(productName) ? null : `*${productName}*`,
      states: state
    };
  } else {
    return {
      "customer.id": customerId,
      "season.id": seasonId,
      products: isEmpty(productName) ? null : `*${productName}*`,
      states: state
    };
  }
};

const filters = (tabIdx, productList, unitList, translate) => (
  <Filter>
    {tabIdx === 0 && unitList?.length > 2 && (
      <SelectInput label={translate("resources.fpo.unit", { _: "Unit" })} source="unitId" choices={unitList} {...inputOptions(12)} />
    )}
    <SelectInput label={translate("resources.fpo.status", { _: "Status" })} source="status" choices={getStateChoices()} {...inputOptions(12)} />

    <SelectInput
      label={translate("resources.fpo.products", { _: "Products" })}
      source="productName"
      choices={productList.map(p => ({ id: p.name, name: p.name }))}
      {...inputOptions(12)}
    />
    {tabIdx === 1 && (
      <SelectInput
        label={translate("resources.fpo.seasonName", { _: "Season" })}
        source="seasonName"
        choices={productList.map(p => ({ id: p.name, name: p.name }))}
        {...inputOptions(12)}
      />
    )}

    {tabIdx === 0 && (
      <ReferenceInput
        label={translate("resources.fpo.seasonName", { _: "Season" })}
        resource="seasons"
        source="seasonId"
        reference="seasons"
        pageSize={100}
        sort={{ field: "name", order: "asc" }}
        {...inputOptions(12)}>
        <SelectInput />
      </ReferenceInput>
    )}
    {tabIdx === 0 && (
      <ReferenceArrayInput
        label={translate("resources.fpo.customers", { _: "Customers" })}
        resource="customers"
        source="customers"
        reference="customers"
        sort={{ field: "name", order: "asc" }}
        idKey="name"
        {...inputOptions(12)}>
        <AutoCompleteArrayInput disablePortal={true} optionValue="name" />
      </ReferenceArrayInput>
    )}
    {tabIdx === 1 && (
      <ReferenceArrayInput
        label={translate("resources.fpo.customerName", { _: "Customer" })}
        resource="customers"
        source="customerId"
        reference="customers"
        sort={{ field: "name", order: "asc" }}
        {...inputOptions(12)}>
        <AutoCompleteArrayInput disablePortal={true} />
      </ReferenceArrayInput>
    )}
  </Filter>
);

const CustomDatagrid = props => {
  const { tabIdx, data, ids, history, fields = [], unitList = [] } = props;

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "fpo", title: "FPO", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "unitId", title: "Unit", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "poRef", title: "PO Ref. No" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "products", title: "Product" },
      { dataKey: "styles", title: "Style" },
      { dataKey: "colors", title: "Color" },
      { dataKey: "inseams", title: "Inseam", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "destinations", title: "Destination", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "deliveryDates", title: "Delivery Date", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "delModes", title: "Delivery Mode", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "customerFlowRefs", title: "BPO", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "flowRefs", title: "Line ID", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "delFlowRefs", title: "Del. Line ID", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "status", title: "Status" }
    ];
    if (tabIdx === 0) {
      cols = cols.filter(c => !["orderQty"].includes(c.dataKey));
    } else {
      cols = cols.filter(c => !["fpo", "unitId", "fpoQty"].includes(c.dataKey));
    }

    return cols.filter(c => fields.includes(c.dataKey));
  }, [tabIdx, fields]);

  const rows = useDeepMemo(() => {
    let rowList = ids.map(id => data[id]);

    if (tabIdx === 0) {
      rowList = rowList.map(row => {
        return {
          ...row,
          customerName: row.customer?.name,
          seasonName: row.season?.name,
          unitId: unitList?.find(e => e.unitId === row.unitId)?.name || row?.unitId,
          deliveryDates: !isEmpty(row.deliveryDates)
            ? listFromCsv(row.deliveryDates)
                .map(date => format(Date.parse(date), "dd MMM yy"))
                .join(", ")
            : "",
          status: isTrue(row.cancelled) ? `Cancelled` : renderState(row)
        };
      });
    } else {
      rowList = rowList.map(row => {
        return {
          ...row,
          unitId: row.primaryUnitId,
          customerName: row.customer?.name,
          seasonName: row.season?.name,
          deliveryDates: !isEmpty(row.deliveryDates)
            ? listFromCsv(row.deliveryDates)
                .map(date => format(Date.parse(date), "dd MMM yy"))
                .join(", ")
            : "",
          status: renderState(row)
        };
      });
    }

    return rowList;
  }, [ids, data, unitList, tabIdx]);

  return (
    <div>
      <Card>
        <MuiTable
          columns={columns}
          rows={rows}
          fontSize={16}
          cellLength={25}
          i18nKey={tabIdx === 0 ? "resources.fpo.fields" : "resources.orders.fields"}
          onRowClick={row => history.push(`/order-mngmt/fpo/${row.orderId || row.id}/view`, { fpo: row.fpo })}
        />
      </Card>
    </div>
  );
};

const FactoryProductionOrder = props => {
  const [tabIdx, setTabIdx] = useSafeState(0);

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, "orders");
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");

  const { allUnitList } = useUnit();
  const history = useHistory();

  const { data: productList = [] } = useQuery({ url: "products", params: { size: 10000 } });

  const fieldList = [...orderFields, ...flowInfoFields, "fpo", "fpoQty", "unitId", "delModes"];

  const unitChoices = [{ id: "all", name: "All Units" }, ...allUnitList.map(({ unitId, name }) => ({ id: unitId, name }))];

  return (
    <div>
      <List
        {...props}
        key={tabIdx}
        resource={tabIdx === 0 ? "fpo" : "orders"}
        i18nKey={tabIdx === 0 ? "fpo" : "orders"}
        title={
          <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
            <SubTab label={props.translate("resources.fpo.name", { _: "FPO", smart_count: 3 })} />
            <SubTab label={props.translate("resources.orders.name", { _: "Order" })} />
          </Tabs>
        }
        searchKeys={
          tabIdx === 0
            ? ["fpo", "poRef", "styles", "styleNos", "flowRefs", "delFlowRefs", "customerFlowRefs"]
            : ["poRef", "customerPoRef", "styles", "flowRefs", "delFlowRefs", "customerFlowRefs"]
        }
        actions={({ resource }) => (
          <div>
            <FilterButton showLabel={false} resource={resource} />
          </div>
        )}
        filters={filters(tabIdx, productList, unitChoices, props.translate)}
        parse={parse(tabIdx)}>
        <CustomDatagrid tabIdx={tabIdx} fields={fieldList} unitList={allUnitList} history={history} />
      </List>
    </div>
  );
};

export default translate(FactoryProductionOrder);
